// src/components/InstituicaoCard.js

import React from 'react';
import PropTypes from 'prop-types';
import {
  Card,
  CardContent,
  Typography,
  Button,
  Box,
  Chip,
  Stack,
  Tooltip
} from '@mui/material';
import {
  AccountCircle as AccountCircleIcon,
  School as SchoolIcon,
  SupervisorAccount as SupervisorAccountIcon,
  Tune as TuneIcon
} from '@mui/icons-material';
import { Link } from 'react-router-dom';
import useAuth from '../../hooks/useAuth';

/**
 * Função para determinar a cor do Chip com base no tipo de perfil.
 * @param {string} tipo - Tipo do perfil.
 * @returns {string} - Cor correspondente para o Chip.
 */
const getPerfilColor = (tipo) => {
  switch (tipo.toUpperCase()) {
    case 'GESTAO':
      return 'primary'; // Cor primária para perfis de gestão
    case 'ADMIN':
      return 'secondary'; // Cor secundária para perfis administrativos
    case 'USUARIO':
      return 'default'; // Cor padrão para outros perfis
    default:
      return 'default';
  }
};

/**
 * Função para determinar o ícone do Chip com base no tipo de perfil.
 * @param {string} tipo - Tipo do perfil.
 * @returns {JSX.Element} - Ícone correspondente para o Chip.
 */
const getPerfilIcon = (tipo) => {
  switch (tipo.toUpperCase()) {
    case 'GESTAO':
      return <SupervisorAccountIcon />;
    case 'ADMIN':
      return <AccountCircleIcon />;
    case 'USUARIO':
      return <AccountCircleIcon />;
    default:
      return <AccountCircleIcon />;
  }
};

const InstituicaoCard = ({ instituicao }) => {
  const { user } = useAuth();
  const isSuperAdmin = user?.tipo === 'SUPER_ADMIN';

  return (
    <Card>
      <CardContent>
        <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
          {instituicao.logo ? (
            <img 
              src={instituicao.logo} 
              alt={instituicao.nome} 
              style={{ width: 50, height: 50, objectFit: 'cover', borderRadius: '50%' }}
            />
          ) : (
            <SchoolIcon sx={{ fontSize: 50 }} />
          )}
          <Box>
            <Typography variant="h5" component="div">
              {instituicao.nome}
            </Typography>
            <Typography color="text.secondary" variant="body2">
              {instituicao.tipoInstituicao}
            </Typography>
          </Box>
        </Stack>

        <Stack spacing={1}>
          <Typography variant="body2">
            <strong>Email:</strong> {instituicao.email}
          </Typography>
          {instituicao.perfis && instituicao.perfis.length > 0 && (
            <Box>
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                <strong>Perfis:</strong> {instituicao.perfis.length}
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap">
                {instituicao.perfis.map((perfil, index) => (
                  <Tooltip key={index} title={perfil.tipo}>
                    <Chip
                      size="small"
                      icon={getPerfilIcon(perfil.tipo)}
                      label={perfil.tipo}
                      variant="outlined"
                      color={getPerfilColor(perfil.tipo)}
                    />
                  </Tooltip>
                ))}
              </Stack>
            </Box>
          )}
        </Stack>

        <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
          <Button 
            component={Link} 
            to={`/instituicoes/${instituicao.id}`} 
            variant="contained" 
            color="primary"
          >
            Ver Detalhes
          </Button>
          <Button
            component={Link}
            to={`/instituicoes/${instituicao.id}/perfis`}
            variant="outlined"
          >
            Gerenciar Perfis
          </Button>
          {isSuperAdmin && (
            <Button
              component={Link}
              to={`/admin/instituicoes/${instituicao.id}`}
              variant="outlined"
              color="secondary"
              startIcon={<TuneIcon />}
            >
              Módulos
            </Button>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

InstituicaoCard.propTypes = {
  instituicao: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    nome: PropTypes.string.isRequired,
    tipoInstituicao: PropTypes.string.isRequired,
    email: PropTypes.string.isRequired,
    logo: PropTypes.string,
    perfis: PropTypes.arrayOf(PropTypes.shape({
      tipo: PropTypes.string.isRequired
    }))
  }).isRequired,
};

export default InstituicaoCard;
